import { useState } from "react";
import { Calendar, Badge, Button, Modal, Form, Input, TimePicker, Checkbox } from "antd";
import type { Dayjs } from "dayjs";
import dayjs from "dayjs";
import { Plus, Loader2, Clock } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useAttendance } from "../../hooks/useAttendance";
import { days, WorkShift } from "@/types/attendance";

const ShiftCalendar = () => {
  const { company } = useAuth();
  const slug = company?.slug || "";
  const { shifts, holidays, isLoadingShifts, isLoadingHolidays, createShift } = useAttendance(slug);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form] = Form.useForm();

  const handleCreateShift = async (values: any) => {
    try {
      const formattedValues = {
        ...values,
        slug,
        start_time: values.start_time.format("HH:mm"),
        end_time: values.end_time.format("HH:mm"),
      };
      await createShift.mutateAsync(formattedValues);
      setIsModalOpen(false);
      form.resetFields();
    } catch (error) {
      // Error handled in hook
    }
  };

  const dateCellRender = (value: Dayjs) => {
    const dateStr = value.format("YYYY-MM-DD");
    const dayOfWeek = value.day(); // 0 (Sun) to 6 (Sat)
    
    const dayHolidays = holidays?.filter((h: any) => h.date === dateStr) || [];
    const dayShifts = shifts?.filter((s: WorkShift) => s.work_days.includes(dayOfWeek)) || [];

    return (
      <div className="flex flex-col gap-0.5 overflow-hidden">
        {dayHolidays.map((h: any) => (
          <Badge 
            key={h.id}
            status="error" 
            title={h.description} 
            text={
              <span className="text-[10px] font-bold text-rose-600 truncate max-w-[80px]">
                {h.description || 'Festivo'}
              </span>
            } 
          />
        ))}
        {dayShifts.map(shift => (
          <div key={shift.id} className="bg-blue-50 border-l-2 border-blue-500 px-1 py-0.5 rounded-sm mb-0.5">
            <p className="text-[9px] font-semibold text-blue-700 truncate m-0 leading-tight">
              {shift.name}
            </p>
            <p className="text-[8px] text-blue-500 m-0 leading-tight">
              {dayjs(`2000-01-01 ${shift.start_time}`).format("h:mm A")} - {dayjs(`2000-01-01 ${shift.end_time}`).format("h:mm A")}
            </p>
          </div>
        ))}
      </div>
    );
  };

  if (isLoadingHolidays || isLoadingShifts) {
    return (
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm flex justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200/60 rounded-2xl p-6 shadow-sm overflow-hidden">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-slate-900">Planificación Mensual</h2>
        <div className="flex gap-2">
          <Button 
            type="primary" 
            className="bg-blue-600 hover:bg-blue-700 border-none h-10 flex items-center gap-2 shadow-lg shadow-blue-500/20 text-white font-medium"
            onClick={() => setIsModalOpen(true)}
          >
            <Plus className="h-4 w-4" />
            Nuevo Turno
          </Button>
        </div>
      </div>
      
      <div className="attendance-calendar">
        <Calendar 
          cellRender={dateCellRender}
          style={{ 
            backgroundColor: 'transparent',
            borderRadius: '12px'
          }}
        />
      </div>

      <Modal
        title={
          <div className="flex items-center gap-2 text-slate-800 pb-2 border-b border-slate-100 font-bold">
            <Clock className="h-5 w-5 text-blue-600" />
            <span>Configurar Nuevo Turno Corporativo</span>
          </div>
        }
        open={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        footer={null}
        width={500}
        className="attendance-modal"
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleCreateShift}
          initialValues={{
            work_days: [1, 2, 3, 4, 5],
            start_time: dayjs("08:00", "HH:mm"),
            end_time: dayjs("17:00", "HH:mm"),
          }}
          className="pt-4"
        >
          <Form.Item
            name="name"
            label={<span className="text-xs font-bold text-slate-500 uppercase">Nombre del Turno</span>}
            rules={[{ required: true, message: 'Por favor ingresa un nombre' }]}
          >
            <Input placeholder="Ej: Diurno Central" className="h-11 rounded-xl bg-slate-50 border-slate-200 transition-all focus:bg-white" />
          </Form.Item>

          <div className="grid grid-cols-2 gap-4">
            <Form.Item
              name="start_time"
              label={<span className="text-xs font-bold text-slate-500 uppercase">Hora entrada</span>}
              rules={[{ required: true }]}
            >
              <TimePicker use12Hours format="h:mm A" className="w-full h-11 rounded-xl bg-slate-50 border-slate-200" />
            </Form.Item>
            <Form.Item
              name="end_time"
              label={<span className="text-xs font-bold text-slate-500 uppercase">Hora salida</span>}
              rules={[{ required: true }]}
            >
              <TimePicker use12Hours format="h:mm A" className="w-full h-11 rounded-xl bg-slate-50 border-slate-200" />
            </Form.Item>
          </div>

          <Form.Item
            name="work_days"
            label={<span className="text-xs font-bold text-slate-500 uppercase">Días Laborales</span>}
            rules={[{ required: true, message: 'Selecciona al menos un día' }]}
          >
            <Checkbox.Group className="w-full">
              <div className="flex flex-wrap gap-2">
                {days.map(day => (
                  <Checkbox key={day.id} value={day.id} className="text-xs bg-slate-50 border border-slate-100 px-3 py-1.5 rounded-lg m-0 ml-0! transition-all hover:bg-white hover:shadow-sm">
                    {day.label}
                  </Checkbox>
                ))}
              </div>
            </Checkbox.Group>
          </Form.Item>

          <div className="flex justify-end gap-3 pt-6 border-t border-slate-100 mt-6">
            <Button onClick={() => setIsModalOpen(false)} className="h-11 px-6 rounded-xl font-bold bg-slate-50 border-slate-200 text-slate-500 hover:bg-slate-100 transition-all">Cancelar</Button>
            <Button 
              type="primary" 
              htmlType="submit" 
              loading={createShift.isPending}
              className="bg-blue-600 hover:bg-blue-700 h-11 px-10 rounded-xl font-bold shadow-lg shadow-blue-500/20 border-none transition-all hover:scale-[1.02] text-white"
            >
              Cargar Turno
            </Button>
          </div>
        </Form>
      </Modal>

      <style>{`
        .attendance-calendar .ant-picker-calendar {
          background: transparent !important;
        }
        .attendance-calendar .ant-picker-calendar-header {
          padding: 12px 0 !important;
        }
        .attendance-calendar .ant-picker-cell-inner {
          border-radius: 12px !important;
          min-height: 110px !important;
          padding: 8px !important;
        }
        .attendance-calendar .ant-picker-calendar-date-today {
          border-top-color: #2563eb !important;
          background: #eff6ff !important;
        }
        .attendance-modal .ant-modal-content {
          border-radius: 24px !important;
          padding: 0 !important;
          overflow: hidden;
        }
        .attendance-modal .ant-modal-header {
          padding: 24px 24px 0 !important;
          margin: 0 !important;
        }
        .attendance-modal .ant-modal-body {
          padding: 0 24px 24px !important;
        }
        .ant-checkbox-wrapper {
          margin-left: 0 !important;
        }
        .ant-checkbox-wrapper-checked {
          background: #eff6ff !important;
          border-color: #3b82f6 !important;
          color: #1d4ed8 !important;
        }
      `}</style>
    </div>
  );
};

export default ShiftCalendar;
